/**
 * Create / edit page for an API protection rule. Reached from the list via
 * `api-protection/new?domain=` and `api-protection/edit?domain=&id=`.
 *
 * A rule matches by a single condition on the request URI (without the query
 * string) and signs a time-limited access token on a custom request parameter,
 * optionally rate-limiting matched requests. The URI operator catalog comes from
 * the firewall schema (`GET /waf/firewall/schema`); the field is fixed to
 * `URI:match_all`. Everything lives on one page for both create and edit.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafFirewallSchema,
  useWafApiProtectionRules,
  useCreateApiProtectionRule,
  useUpdateApiProtectionRule,
  wafExtractError,
  type APIProtectionRule,
  type APIProtectionAction,
  type APIProtectionRuleInput,
  type ATPCondition,
  type FirewallField,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WafApiProtection.module.css';

/** The fixed match field — request URI without query string. */
const URI_FIELD = 'URI:match_all';

/** Validation bounds (mirrors the vendor's API protection form). */
const KEY_MIN = 16;
const KEY_MAX = 64;
const TTL_MIN = 60;
const TTL_MAX = 86400;
const PARAM_MIN = 1;
const PARAM_MAX = 8;
const QUERIES_MIN = 1;
const QUERIES_MAX = 99999;
const BLOCK_MIN = 1;
const BLOCK_MAX = 86400;
const DEFAULT_BLOCK_TIME = 60;

function Field({
  label,
  hint,
  invalid,
  children,
}: {
  label: string;
  hint?: string;
  invalid?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint ? (
        <span
          className={styles.subHint}
          style={invalid ? { color: 'var(--vn-danger-text, var(--vn-color-red-500))' } : undefined}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function WafApiProtectionForm({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const id = params.get('id');
  const isEdit = !!id;
  const back = () => navigate(`/services/waf/${serviceId}/api-protection`);

  const schemaQ = useWafFirewallSchema(serviceId);
  const fields = schemaQ.data?.fields ?? [];

  // The URI field's operator catalog (the field itself is fixed).
  const uriField = useMemo<FirewallField | undefined>(
    () => fields.find((f) => f.value === URI_FIELD) ?? fields.find((f) => /uri/i.test(f.value) || /uri/i.test(f.label)),
    [fields],
  );
  const uriFieldValue = uriField?.value ?? URI_FIELD;
  const operators = uriField?.operators ?? [];
  const defaultOperator = useMemo(
    () => operators.find((o) => /contain/i.test(`${o.value} ${o.label}`))?.value ?? operators[0]?.value ?? '',
    [operators],
  );

  const rulesQ = useWafApiProtectionRules(serviceId, isEdit ? domain : undefined);
  const rule = useMemo<APIProtectionRule | undefined>(
    () => (isEdit ? (rulesQ.data ?? []).find((r) => r.id === id) : undefined),
    [isEdit, rulesQ.data, id],
  );

  // ---- form state ----
  const [seeded, setSeeded] = useState(false);
  const [name, setName] = useState('');
  const [operator, setOperator] = useState('');
  const [value, setValue] = useState('');
  const [key, setKey] = useState('');
  const [tokenTtl, setTokenTtl] = useState('');
  const [param, setParam] = useState('');
  const [rateLimit, setRateLimit] = useState(false);
  const [rlQueries, setRlQueries] = useState('');
  const [blockTime, setBlockTime] = useState(String(DEFAULT_BLOCK_TIME));
  // Validation hints stay hidden until the first submit attempt.
  const [submitted, setSubmitted] = useState(false);

  // Seed once the schema (and, on edit, the rule) is ready.
  useEffect(() => {
    if (seeded) return;
    if (schemaQ.isLoading || fields.length === 0) return;
    if (isEdit) {
      if (rulesQ.isLoading) return;
      if (rule) seedFrom(rule);
      setSeeded(true);
    } else {
      setOperator(defaultOperator);
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded, schemaQ.isLoading, fields.length, isEdit, rule, rulesQ.isLoading, defaultOperator]);

  function seedFrom(r: APIProtectionRule) {
    setName(r.name);
    // The URI condition (first / the one on the URI field).
    const cond = (r.conditions ?? []).find((c) => c.field === uriFieldValue) ?? (r.conditions ?? [])[0];
    setOperator(cond?.operator || defaultOperator);
    setValue(cond?.value ?? '');
    const a = r.action;
    setParam(a.param ?? '');
    setKey(a.key ?? '');
    if (a.token_ttl != null) setTokenTtl(String(a.token_ttl));
    setRateLimit(!!a.rate_limit_enable);
    if (a.rate_limit_queries != null) setRlQueries(String(a.rate_limit_queries));
    setBlockTime(String(a.rate_limit_block_time || DEFAULT_BLOCK_TIME));
  }

  // ---- validation ----
  const nameBad = !name.trim();
  const operatorBad = !operator;
  const valueBad = !value.trim();
  const keyLen = key.trim().length;
  const keyBad = keyLen < KEY_MIN || keyLen > KEY_MAX;
  const ttlNum = Number(tokenTtl);
  const ttlBad = !Number.isFinite(ttlNum) || ttlNum < TTL_MIN || ttlNum > TTL_MAX;
  const paramLen = param.trim().length;
  const paramBad = paramLen < PARAM_MIN || paramLen > PARAM_MAX;
  const queriesNum = Number(rlQueries);
  const queriesBad = rateLimit && (!Number.isFinite(queriesNum) || queriesNum < QUERIES_MIN || queriesNum > QUERIES_MAX);
  const blockNum = Number(blockTime);
  const blockBad = rateLimit && (!Number.isFinite(blockNum) || blockNum < BLOCK_MIN || blockNum > BLOCK_MAX);

  const valid = !nameBad && !operatorBad && !valueBad && !keyBad && !ttlBad && !paramBad && !queriesBad && !blockBad;

  function buildAction(): APIProtectionAction {
    return {
      type: 'apiProtection',
      param: param.trim(),
      key: key.trim(),
      token_ttl: ttlNum || 0,
      rate_limit_enable: rateLimit,
      rate_limit_queries: rateLimit ? queriesNum || 0 : 0,
      rate_limit_block_time: rateLimit ? blockNum || DEFAULT_BLOCK_TIME : 0,
    };
  }

  function buildConditions(): ATPCondition[] {
    return [{ field: uriFieldValue, operator, value: value.trim() }];
  }

  const create = useCreateApiProtectionRule(serviceId, {
    onSuccess: (data) => {
      show({ tone: 'success', title: 'Rule created' });
      trackFeatureAction('waf', 'api_rule', 'create');
      // The vendor's "Implement script" button lands on the rule's detail (with
      // the token scripts). Fall back to the list if the id isn't returned.
      const created = data as APIProtectionRule | undefined;
      if (created?.id) {
        navigate(`/services/waf/${serviceId}/api-protection/detail?domain=${encodeURIComponent(domain)}&id=${encodeURIComponent(created.id)}`);
      } else {
        back();
      }
    },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateApiProtectionRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule updated' }); trackFeatureAction('waf', 'api_rule', 'edit'); back(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const pending = create.isPending || update.isPending;

  const submit = () => {
    setSubmitted(true);
    if (!valid) return;
    const input: APIProtectionRuleInput = {
      name: name.trim(),
      enable: rule ? rule.enable : true,
      action: buildAction(),
      conditions: buildConditions(),
    };
    if (isEdit && rule) update.mutate({ domain, id: rule.id, input });
    else create.mutate({ domain, input });
  };

  const header = (
    <PageHeader
      icon={<KeyRound size={22} />}
      title={`${isEdit ? 'Edit' : 'Add'} API protection rule`}
      description="Customise and configure your own API protection rule to keep your API requests secured."
      actions={<Button variant="secondary" startIcon={<ArrowLeft size={15} />} onClick={back}>Back</Button>}
    />
  );

  if (!domain) {
    return (
      <Container size="lg">{header}
        <Alert tone="warning" title="No domain selected" actions={<button onClick={back}>Back</button>}>
          Open this form from the API Protection list so a domain is selected.
        </Alert>
      </Container>
    );
  }
  if (schemaQ.isError) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Couldn't load the rule builder" actions={<button onClick={() => schemaQ.refetch()}>Retry</button>}>
          The WAF service couldn't return the rule schema. Try again.
        </Alert>
      </Container>
    );
  }
  if (!seeded) {
    return <Container size="lg">{header}<Skeleton style={{ height: 460 }} /></Container>;
  }
  if (isEdit && !rule) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Rule not found" actions={<button onClick={back}>Back</button>}>This rule no longer exists.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      {header}
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Edit' : 'New'} API protection rule</CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Rule name">
              <Input
                value={name}
                placeholder="Rule name"
                disabled={pending}
                invalid={submitted && nameBad}
                onChange={(e) => setName(e.target.value)}
                style={{ maxWidth: 360 }}
              />
            </Field>

            <Field
              label="URI in REQUEST w/o query string"
              hint="*The file path should not contain static content for the protected page to display correctly."
              invalid={submitted && (operatorBad || valueBad)}
            >
              <div className={styles.condBottom}>
                <Select value={operator} onValueChange={setOperator} disabled={pending || !operators.length}>
                  <SelectTrigger aria-label="Operator" className={styles.condOp}>
                    <SelectValue placeholder="Operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className={styles.condValue}
                  value={value}
                  placeholder="Value"
                  disabled={pending}
                  invalid={submitted && (operatorBad || valueBad)}
                  onChange={(e) => setValue(e.target.value)}
                  aria-label="Value"
                />
              </div>
            </Field>

            <Field
              label="Key"
              hint={`* The length should be between ${KEY_MIN} to ${KEY_MAX} characters`}
              invalid={submitted && keyBad}
            >
              <Input
                value={key}
                placeholder="The main validation method between your services and VNIS"
                disabled={pending}
                invalid={submitted && keyBad}
                onChange={(e) => setKey(e.target.value)}
              />
            </Field>

            <Field label="Token lifespan" hint={`* Enter a number between ${TTL_MIN} to ${TTL_MAX}`} invalid={submitted && ttlBad}>
              <div className={styles.suffixWrap}>
                <Input
                  type="number"
                  min={TTL_MIN}
                  max={TTL_MAX}
                  className={styles.noSpin}
                  value={tokenTtl}
                  disabled={pending}
                  invalid={submitted && ttlBad}
                  onChange={(e) => setTokenTtl(e.target.value)}
                  aria-label="Token lifespan"
                />
                <span className={styles.suffix}>sec.</span>
              </div>
            </Field>

            <Field
              label="Custom parameter"
              hint={`* The length should be between ${PARAM_MIN} to ${PARAM_MAX} characters`}
              invalid={submitted && paramBad}
            >
              <Input
                value={param}
                placeholder="Define your own parameter name for the exchanged token"
                disabled={pending}
                invalid={submitted && paramBad}
                onChange={(e) => setParam(e.target.value)}
                style={{ maxWidth: 360 }}
              />
            </Field>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Switch checked={rateLimit} disabled={pending} aria-label="Rate Limit" onCheckedChange={setRateLimit} />
              <span className={styles.fieldLabel}>Rate Limit</span>
            </div>

            {rateLimit && (
              <>
                <Field label="Number of queries" hint={`* Enter a number between ${QUERIES_MIN} to ${QUERIES_MAX}`} invalid={submitted && queriesBad}>
                  <div className={styles.suffixWrap}>
                    <Input
                      type="number"
                      min={QUERIES_MIN}
                      max={QUERIES_MAX}
                      className={styles.noSpin}
                      value={rlQueries}
                      disabled={pending}
                      invalid={submitted && queriesBad}
                      onChange={(e) => setRlQueries(e.target.value)}
                      aria-label="Number of queries"
                    />
                    <span className={styles.suffix}>/ min.</span>
                  </div>
                </Field>

                <Field label="Block time" hint={`* Enter a number between ${BLOCK_MIN} to ${BLOCK_MAX}`} invalid={submitted && blockBad}>
                  <div className={styles.suffixWrap}>
                    <Input
                      type="number"
                      min={BLOCK_MIN}
                      max={BLOCK_MAX}
                      className={styles.noSpin}
                      value={blockTime}
                      disabled={pending}
                      invalid={submitted && blockBad}
                      onChange={(e) => setBlockTime(e.target.value)}
                      aria-label="Block time"
                    />
                    <span className={styles.suffix}>sec.</span>
                  </div>
                </Field>
              </>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', justifyContent: 'flex-end', marginTop: 'var(--vn-space-2)' }}>
              {submitted && !valid ? (
                <span style={{ marginRight: 'auto', color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-sm)' }}>
                  Please complete the highlighted fields.
                </span>
              ) : null}
              <Button variant="secondary" onClick={back} disabled={pending}>Cancel</Button>
              <Button onClick={submit} disabled={pending}>{isEdit ? 'Save changes' : 'Implement script'}</Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </Container>
  );
}

export default WafApiProtectionForm;
